import {useNavigate} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {useServices} from '@/services/context'
import type {ConnectionTest, PreviewResponse, SyncTriggerResponse} from '@/types/inbound-connector'

const QUERY_KEY = 'inbound-connectors'
const SYNC_STATUS_KEY = 'inbound-connectors-sync-status'
const SYNC_RUNS_KEY = 'inbound-connectors-sync-runs'

export interface UseInboundConnectorMutationsOptions {
    onTriggerSyncSuccess?: () => void
    onRunPreviewSuccess?: (response: PreviewResponse) => void
    onRunPreviewError?: () => void
    onTestConnectionSuccess?: (result: ConnectionTest) => void
    onTestConnectionError?: () => void
}

/**
 * Composite mutations hook for the InboundConnectorDetail page. Bundles
 * every write operation against {@code services.inboundConnectors.*} so the
 * page body never has to derive query-invalidation strings or success-toast
 * keys inline. Each returned member is a stock TanStack mutation result
 * with consistent invalidation:
 *
 * - {@code enable}, {@code disable}: invalidates the detail query.
 * - {@code triggerSync}: invalidates detail + sync-status + sync-runs so a
 *   newly-triggered run becomes visible on the SyncRunsTab.
 * - {@code runPreview}, {@code testConnection}: no invalidation — they
 *   surface their result through {@code onSuccess}.
 * - {@code deleteConnector}: invalidates the list and navigates back to
 *   {@link routes.connectors.inbound.list} via the route helper (no
 *   substring matching at the call site).
 */
export function useInboundConnectorMutations(
    id: string | undefined,
    options: UseInboundConnectorMutationsOptions = {},
) {
    const services = useServices()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const i18n = useI18n()
    const {
        onTriggerSyncSuccess,
        onRunPreviewSuccess,
        onRunPreviewError,
        onTestConnectionSuccess,
        onTestConnectionError,
    } = options

    const enable = useEntityMutation({
        mutationFn: () => services.inboundConnectors.enable(id!),
        invalidate: [[QUERY_KEY, id], [QUERY_KEY]],
        successMessage: i18n.inboundConnectors.enableSuccess,
    })

    const disable = useEntityMutation({
        mutationFn: () => services.inboundConnectors.disable(id!),
        invalidate: [[QUERY_KEY, id], [QUERY_KEY]],
        successMessage: i18n.inboundConnectors.disableSuccess,
    })

    const syncMutationShared = {
        invalidate: [[QUERY_KEY, id], [SYNC_STATUS_KEY, id], [SYNC_RUNS_KEY, id]],
        successMessage: i18n.inboundConnectors.syncStarted,
        onSuccess: () => {
            onTriggerSyncSuccess?.()
        },
    }

    const triggerSync = useEntityMutation<SyncTriggerResponse, Error, 'full'>({
        mutationFn: () => services.inboundConnectors.syncFull(id!),
        ...syncMutationShared,
    })

    const triggerDeltaSync = useEntityMutation<SyncTriggerResponse, Error, void>({
        mutationFn: () => services.inboundConnectors.syncDelta(id!),
        ...syncMutationShared,
    })

    const runPreview = useEntityMutation<PreviewResponse, Error, void>({
        mutationFn: () => services.inboundConnectors.syncPreview(id!),
        onSuccess: (response) => {
            onRunPreviewSuccess?.(response)
        },
        onError: () => {
            onRunPreviewError?.()
        },
    })

    const testConnection = useEntityMutation<ConnectionTest, Error, void>({
        mutationFn: () => services.inboundConnectors.test(id!),
        onSuccess: (result) => {
            onTestConnectionSuccess?.(result)
        },
        onError: () => {
            onTestConnectionError?.()
        },
    })

    const deleteConnector = useEntityMutation<void, Error, void>({
        mutationFn: () => services.inboundConnectors.remove(id!),
        invalidate: [QUERY_KEY],
        successMessage: i18n.inboundConnectors.deleteSuccess,
        onSuccess: () => {
            // WHY: drop the soon-to-404 detail entry from cache before the
            // detail page unmounts. The bulk invalidate prefix-matches every
            // detail query rooted at [QUERY_KEY], which would refetch the
            // just-deleted id, hit GN0002, and flash an error banner during
            // the navigation transition. Removing the per-id queries first
            // prevents the refetch entirely.
            queryClient.removeQueries({queryKey: [QUERY_KEY, id]})
            queryClient.removeQueries({queryKey: [SYNC_STATUS_KEY, id]})
            queryClient.removeQueries({queryKey: [SYNC_RUNS_KEY, id]})
            navigate(routes.connectors.inbound.list())
        },
    })

    return {enable, disable, triggerSync, triggerDeltaSync, runPreview, testConnection, deleteConnector}
}
